// The guided P&P screens' words, in the person's own language (Settings → My profile; on a shared screen the
// language of the person who scanned their badge / entered their PIN). Fernando (2026-10-16): "All the more
// complicated steps should also have instructions that can later be translated for Russian only workforce".
// ET and RU were written by Claude and are to be checked by Fernando. (Guided P&P phase 4, 2026-10-17)
import { Lang } from '../packingText';

export interface GuidedTexts {
  scanBadge: string;
  scanBadgeSub: string;
  signOut: string;
  hello: (name: string) => string;
  ready: (n: number) => string;
  readyLabel: string;
  priorityFirst: (n: number) => string;
  howMany: string;
  all: string;
  custom: string;
  start: string;
  starting: string;
  pickup: (time: string) => string;
  noPickup: string;
  notReadyTitle: string;
  roundsNow: string;
  noRounds: string;
  phase: Record<string, string>;
  goToTablet: (rid: number) => string;
  goToTabletSub: string;
  notesPrinting: string;
  notesPrinted: string;
  notesFailed: (why: string) => string;
  printerJam: string;
  printerJamManager: string;
  ordersPage: string;
  reprinted: string;
  progress: (stop: number, total: number) => string;
  packHere: (rid: number) => string;
  packHereSub: string;
  packed: (p: number, t: number) => string;
  // 1.11: orders moved out at packing are named, not subtracted ("19 of 18 packed" on round 661)
  diverted: (n: number) => string;
  // 1.11: Finish is held back by a packed order whose label was never scanned (Elsa, round 662, NO28551)
  labelPending: (orders: string) => string;
  // 2.11: a manager's fix at a station — each order processed at its label scan
  fixTitle: (id: number) => string;
  fixProgress: (done: number, total: number) => string;
  fixLeft: (orders: string) => string;
  endFix: string;
  endFixConfirm: string;
  fixDone: (n: number) => string;
  fixManagerTitle: string;
  fixLeftWhere: (n: number) => string;
  // 1.11: a badge read / a PIN accepted — the box says so while the server works (a round start takes 10+ s)
  identifyBusy: (how: 'badge' | 'pin') => string;
  // 1.11: under every identify title — "scan a badge or enter a PIN"
  badgeOrPin: string;
  // 3.11: the Packeta handover — the glow before the pickup, the call at it (between two orders), its screen
  packetaButton: string;
  packetaSoon: (time: string) => string;
  packetaDueTitle: (time: string) => string;
  packetaDueSub: (n: number) => string;
  packetaStart: string;
  packetaHeld: string;
  packetaOnIt: (name: string) => string;
  packetaChip: (count: number, waiting: number) => string;
  packetaBack: string;
  packetaDoneForNow: string;
  packetaCourierTook: (n: number) => string;
  packetaFor: (code: string, day: string) => string;
  packetaStale: (code: string, day: string) => string;
  packetaStaleYes: string;
  packetaStaleNo: string;
  packetaFinishOrder: string;
  finish: string;
  boxCheck: string;
  finishing: string;
  finished: (n: number) => string;
  backToStart: string;
  // tablet
  chooseRound: string;
  noWaiting: string;
  roundOf: (name: string, n: number) => string;
  startedAt: (t: string, station: number | null) => string;
  someoneElse: string;
  whoAreYou: string;
  enterPin: (name: string) => string;
  cancel: string;
  yourRound: (rid: number) => string;
  continueRound: string;
  pausedTitle: string;
  doneTitle: string;
  doneSub: string;
  notRegistered: string;
  // who is acting (19.10: badge or PIN at Start and at Start packing) + the Deleted orders box
  signIn: string;
  signInTitle: string;
  whoStarts: string;
  packTitle: (rid: number) => string;
  startPacking: string;
  continuePacking: string;
  pickedReady: (rid: number) => string;
  orScanBadge: string;
  chooseName: string;
  notYou: (name: string) => string;
  boxTitle: (order: string, why: string) => string;
  boxDone: string;
  // an order moved to Product exchange mid-round: its pieces go to a manager (24.10)
  managerTitle: (order: string, tab: string) => string;
  managerDone: string;
  abandonedTitle: string;
  nothingPicked: string;
  // 19.10: identify — a PIN or a badge, never a name
  identifyStation: string;
  identifyTablet: string;
  startPP: string;
  pressFirst: string;
  scanNotNow: string;
  approveTitle: string;
  approveSub: string;
  otherStation: (n: number) => string;
  packHereBtn: string;
  chooseRoundTitle: string;
  waitingInfo: (n: number) => string;
  boxButton: (n: number) => string;
  boxScreenTitle: string;
  boxScreenSub: string;
  boxPieces: (n: number) => string;
  boxEmpty: string;
  shelvedTo: (code: string, shelf: string) => string;
  onTheShelf: string;
  noShelf: string;
  inBoxHint: (order: string) => string;
  cantFindReported: (item: string, orders: string) => string;
}

// the box at the P&P station (Fernando + Henrik 19.10) — its name as written on the box, in every language
const BOX = 'Deleted orders';


const T: Record<Lang, GuidedTexts> = {
  en: {
    scanBadge: 'Scan your badge',
    scanBadgeSub: 'Hold your badge to the scanner to start picking & packing',
    signOut: 'Sign out',
    hello: n => `Hello, ${n}`,
    ready: n => `${n} orders ready`,
    readyLabel: 'orders ready',
    priorityFirst: n => `${n} resend${n === 1 ? '' : 's'} first`,
    howMany: 'Select how many orders you want to pick and pack.',
    all: 'All',
    custom: 'Other',
    start: 'Start',
    starting: 'Starting…',
    pickup: t => `pickup ${t}`,
    noPickup: 'no pickup known',
    notReadyTitle: 'Not ready (a manager fixes these)',
    roundsNow: 'Rounds now',
    noRounds: 'No open rounds',
    phase: { waiting_picker: 'waiting for a picker', picking: 'picking', paused: 'paused', picked: 'picked — ready to pack',
             packing: 'packing', starting: 'starting', closed: 'finished', cancelled: 'cancelled' },
    goToTablet: rid => `Round ${rid} — fill the cart with boxes and start picking on its tablet`,
    goToTabletSub: 'The delivery notes stay here on the table for packing. On the cart\'s tablet, enter your PIN.',
    notesPrinting: 'Delivery notes are printing…',
    notesPrinted: 'Delivery notes printed ✓',
    notesFailed: w => `Delivery notes did not print: ${w}`,
    printerJam: 'Printer jam — print again',
    printerJamManager: 'Printer jam? Call a manager',
    ordersPage: '← Orders page',
    reprinted: 'Sent to the printer again',
    progress: (s, t) => `stop ${s} of ${t}`,
    packHere: rid => `Round ${rid} is picked — pack it here`,
    packHereSub: 'Scan the first delivery note.',
    packed: (p, t) => `${p} of ${t} packed`,
    diverted: n => ` · ${n} moved out of the round`,
    labelPending: orders => `${orders}: packed, label not scanned — scan the parcel label to finish`,
    fixTitle: id => `Fix ${id}`,
    fixProgress: (d, t) => `${d} of ${t} processed`,
    fixLeft: o => `Still to do: ${o}`,
    endFix: 'End fix',
    endFixConfirm: 'End the fix? The orders not processed stay where they are.',
    fixDone: n => `Fix done — ${n} orders processed`,
    fixManagerTitle: 'Fix orders — a manager',
    fixLeftWhere: n => ` · ${n} left where they were`,
    identifyBusy: how => (how === 'badge' ? 'Badge read ✓ — starting…' : 'PIN accepted ✓ — starting…'),
    badgeOrPin: 'Scan your badge or enter your PIN',
    packetaButton: 'Packeta handover',
    packetaSoon: t => `Soon time to prepare the Packeta parcels for pickup (${t})`,
    packetaDueTitle: t => `${t} — the Packeta courier comes`,
    packetaDueSub: n => `Hand over the ${n} Packeta parcel${n === 1 ? '' : 's'} now: scan each one. Then you go on packing.`,
    packetaStart: 'Start Packeta handover',
    packetaHeld: 'Start the Packeta handover first',
    packetaOnIt: name => `${name} is doing the Packeta handover`,
    packetaChip: (c, w) => `Packeta ${c}${w ? ` · +${w} to add` : ''}`,
    packetaBack: 'Back to packing',
    packetaDoneForNow: 'Done for now',
    packetaCourierTook: n => `Courier took them ✓ (${n})`,
    packetaFor: (code, day) => `${code} · for ${day}`,
    packetaStale: (code, day) => `Did the courier take ${code} on ${day}?`,
    packetaStaleYes: 'Yes, it went',
    packetaStaleNo: 'No — it is still here',
    packetaFinishOrder: 'Finish this order first — then the Packeta handover',
    finish: 'Finish round',
    boxCheck: 'I checked the boxes: no item and no delivery note is left over',
    finishing: 'Finishing…',
    finished: n => `Round finished — ${n} orders processed`,
    backToStart: 'Back to start',
    chooseRound: 'Tap your round',
    noWaiting: 'No round is waiting. Start one at the packing station.',
    roundOf: (n, c) => `${n} · ${c} orders`,
    startedAt: (t, s) => `started ${t}${s ? ` at station ${s}` : ''}`,
    someoneElse: 'Someone else is picking it',
    whoAreYou: 'Who is picking?',
    enterPin: n => `${n}, enter your PIN`,
    cancel: 'Cancel',
    yourRound: rid => `Round ${rid} is on this cart`,
    continueRound: 'Continue',
    pausedTitle: 'Paused — enter the PIN to continue',
    doneTitle: 'Picking done ✓',
    doneSub: 'Take the cart to the packing station, press Start packing there and identify yourself.',
    notRegistered: 'This screen is not registered as a P&P station or a cart tablet. An admin registers it in Settings → Badges & rights → Devices.',
    signIn: 'Sign in',
    signInTitle: 'Sign in — scan your badge or enter your PIN',
    whoStarts: 'Who is starting? Scan your badge or enter your PIN',
    packTitle: rid => `Round ${rid} — scan your badge or enter your PIN to pack`,
    startPacking: 'Start packing',
    continuePacking: 'Continue packing',
    pickedReady: rid => `Round ${rid} is picked — start packing`,
    orScanBadge: 'or scan your badge',
    chooseName: 'Choose your name',
    notYou: n => `Not ${n}?`,
    boxTitle: (o, why) => `Order ${o} left the round${why} — put its pieces in the “${BOX}” box:`,
    boxDone: 'Done — they are in the box',
    managerTitle: (o, t) => `Order ${o} is now a ${t} — give its delivery note and these pieces to a manager:`,
    managerDone: 'Given to a manager',
    abandonedTitle: `Round cancelled — put everything already picked in the “${BOX}” box:`,
    nothingPicked: 'Nothing was picked yet.',
    identifyStation: 'Identify yourself',
    identifyTablet: 'Identify yourself. Please enter your PIN.',
    startPP: 'Start picking & packing',
    pressFirst: 'Press Start picking & packing or Start packing first — then identify yourself',
    scanNotNow: 'Nothing to scan right now',
    approveTitle: 'You don\'t have the right to pack items manually. Invite a manager.',
    approveSub: 'The manager enters their PIN or scans their badge here — they have been told you are waiting. No manager around? Cancel and use Issue → Can\'t find.',
    otherStation: n => `You started this round at station ${n}. Pack it here?`,
    packHereBtn: 'Pack here',
    chooseRoundTitle: 'Which round?',
    waitingInfo: n => (n === 1 ? '1 round is waiting for a picker' : `${n} rounds are waiting for a picker`),
    boxButton: n => `${BOX} box (${n})`,
    boxScreenTitle: `${BOX} box`,
    boxScreenSub: 'Scan a piece from the box (or tap its row), put it on the shelf shown, and it is off the list.',
    boxPieces: n => `${n} piece(s)`,
    boxEmpty: 'The box is empty.',
    shelvedTo: (c, sh) => `${c} → shelf ${sh} ✓`,
    onTheShelf: 'On the shelf',
    noShelf: 'no shelf known',
    inBoxHint: o => `It may be in the “${BOX}” box at the packing station (from order ${o}).`,
    cantFindReported: (i, o) => `OK — ${i}${o ? ` (${o})` : ''}: reported, a manager has been told. Carry on picking — the order is decided at packing.`,
  },
  et: {
    scanBadge: 'Skanni oma kaart',
    scanBadgeSub: 'Hoia oma töötajakaarti skanneri ees, et alustada komplekteerimist ja pakkimist',
    signOut: 'Logi välja',
    hello: n => `Tere, ${n}`,
    ready: n => `${n} tellimust valmis`,
    readyLabel: 'tellimust valmis',
    priorityFirst: n => `${n} uuesti saatmist enne`,
    howMany: 'Vali, mitu tellimust soovid korjata ja pakkida.',
    all: 'Kõik',
    custom: 'Muu',
    start: 'Alusta',
    starting: 'Alustan…',
    pickup: t => `kuller ${t}`,
    noPickup: 'kulleri aeg teadmata',
    notReadyTitle: 'Pole valmis (juhataja parandab)',
    roundsNow: 'Praegused ringid',
    noRounds: 'Avatud ringe pole',
    phase: { waiting_picker: 'ootab komplekteerijat', picking: 'komplekteerimisel', paused: 'pausil', picked: 'komplekteeritud — valmis pakkimiseks',
             packing: 'pakkimisel', starting: 'algab', closed: 'lõpetatud', cancelled: 'tühistatud' },
    goToTablet: rid => `Ring ${rid} — pane kärule kastid ja alusta komplekteerimist käru tahvlis`,
    goToTabletSub: 'Saatelehed jäävad siia lauale pakkimiseks. Käru tahvlis sisesta oma PIN.',
    notesPrinting: 'Saatelehed prinditakse…',
    notesPrinted: 'Saatelehed prinditud ✓',
    notesFailed: w => `Saatelehed ei prinditud: ${w}`,
    printerJam: 'Printeris paberiummistus — prindi uuesti',
    printerJamManager: 'Printeris paberiummistus? Kutsu juht',
    ordersPage: '← Tellimuste leht',
    reprinted: 'Saadeti uuesti printerisse',
    progress: (s, t) => `peatus ${s} / ${t}`,
    packHere: rid => `Ring ${rid} on komplekteeritud — paki see siin`,
    packHereSub: 'Skanni esimene saateleht.',
    packed: (p, t) => `${p} / ${t} pakitud`,
    diverted: n => ` · ${n} ringist välja viidud`,
    labelPending: orders => `${orders}: pakitud, silt skannimata — skanni paki silt, et lõpetada`,
    fixTitle: id => `Parandus ${id}`,
    fixProgress: (d, t) => `${d} / ${t} töödeldud`,
    fixLeft: o => `Veel teha: ${o}`,
    endFix: 'Lõpeta parandus',
    endFixConfirm: 'Lõpetada parandus? Töötlemata tellimused jäävad sinna, kus nad on.',
    fixDone: n => `Parandus tehtud — ${n} tellimust töödeldud`,
    fixManagerTitle: 'Paranda tellimused — juhataja',
    fixLeftWhere: n => ` · ${n} jäi sinna, kus oli`,
    identifyBusy: how => (how === 'badge' ? 'Kaart loetud ✓ — alustan…' : 'PIN vastu võetud ✓ — alustan…'),
    badgeOrPin: 'Skanni oma kaart või sisesta PIN',
    packetaButton: 'Packeta üleandmine',
    packetaSoon: t => `Varsti on aeg Packeta pakid äraviimiseks valmis panna (${t})`,
    packetaDueTitle: t => `${t} — Packeta kuller tuleb`,
    packetaDueSub: n => `Anna nüüd üle ${n} Packeta pakki: skanni igaüks. Siis jätkad pakkimist.`,
    packetaStart: 'Alusta Packeta üleandmist',
    packetaHeld: 'Alusta kõigepealt Packeta üleandmist',
    packetaOnIt: name => `${name} teeb Packeta üleandmist`,
    packetaChip: (c, w) => `Packeta ${c}${w ? ` · +${w} lisada` : ''}`,
    packetaBack: 'Tagasi pakkima',
    packetaDoneForNow: 'Praegu valmis',
    packetaCourierTook: n => `Kuller võttis need ✓ (${n})`,
    packetaFor: (code, day) => `${code} · ${day} jaoks`,
    packetaStale: (code, day) => `Kas kuller võttis ${code} ${day}?`,
    packetaStaleYes: 'Jah, läks',
    packetaStaleNo: 'Ei — on veel siin',
    packetaFinishOrder: 'Lõpeta enne see tellimus — siis Packeta üleandmine',
    finish: 'Lõpeta ring',
    boxCheck: 'Kontrollisin kaste: ühtegi toodet ega saatelehte ei jäänud üle',
    finishing: 'Lõpetan…',
    finished: n => `Ring lõpetatud — ${n} tellimust töödeldud`,
    backToStart: 'Tagasi algusesse',
    chooseRound: 'Vajuta oma ringile',
    noWaiting: 'Ükski ring ei oota. Alusta ringi pakkimislauas.',
    roundOf: (n, c) => `${n} · ${c} tellimust`,
    startedAt: (t, s) => `alustatud ${t}${s ? `, laud ${s}` : ''}`,
    someoneElse: 'Komplekteerib keegi teine',
    whoAreYou: 'Kes komplekteerib?',
    enterPin: n => `${n}, sisesta oma PIN`,
    cancel: 'Tühista',
    yourRound: rid => `Ring ${rid} on selles kärus`,
    continueRound: 'Jätka',
    pausedTitle: 'Paus — jätkamiseks sisesta PIN',
    doneTitle: 'Komplekteerimine tehtud ✓',
    doneSub: 'Vii käru pakkimislauda, vajuta seal „Alusta pakkimist“ ja sisesta oma PIN.',
    notRegistered: 'See ekraan ei ole registreeritud pakkimislaua ega käru tahvlina. Admin registreerib selle: Seaded → Badges & rights → Devices.',
    signIn: 'Logi sisse',
    signInTitle: 'Sisselogimine — skanni oma kaart või sisesta PIN',
    whoStarts: 'Kes alustab? Skanni oma kaart või sisesta PIN',
    packTitle: rid => `Ring ${rid} — pakkimiseks skanni oma kaart või sisesta PIN`,
    startPacking: 'Alusta pakkimist',
    continuePacking: 'Jätka pakkimist',
    pickedReady: rid => `Ring ${rid} on komplekteeritud — alusta pakkimist`,
    orScanBadge: 'või skanni oma kaart',
    chooseName: 'Vali oma nimi',
    notYou: n => `Ei ole ${n}?`,
    boxTitle: (o, why) => `Tellimus ${o} lahkus ringist${why} — pane selle tooted kasti „${BOX}“:`,
    boxDone: 'Tehtud — need on kastis',
    managerTitle: (o, t) => `Tellimus ${o} on nüüd ${t} — anna selle saateleht ja need tooted juhile:`,
    managerDone: 'Antud juhile',
    abandonedTitle: `Ring tühistatud — pane kõik juba võetud tooted kasti „${BOX}“:`,
    nothingPicked: 'Midagi pole veel võetud.',
    identifyStation: 'Tuvasta ennast',
    identifyTablet: 'Tuvasta ennast. Sisesta oma PIN.',
    startPP: 'Alusta komplekteerimist ja pakkimist',
    pressFirst: 'Vajuta enne „Alusta komplekteerimist ja pakkimist“ või „Alusta pakkimist“ — siis tuvasta ennast',
    scanNotNow: 'Praegu pole midagi skannida',
    approveTitle: 'Sul ei ole õigust tooteid käsitsi pakkida. Kutsu juht.',
    approveSub: 'Juht sisestab siia oma PIN-i või skannib kaardi — talle on teada antud, et ootad. Juhti pole? Tühista ja kasuta Issue → Can\'t find.',
    otherStation: n => `Alustasid seda ringi laua ${n} juures. Pakid siin?`,
    packHereBtn: 'Paki siin',
    chooseRoundTitle: 'Milline ring?',
    waitingInfo: n => (n === 1 ? '1 ring ootab komplekteerijat' : `${n} ringi ootab komplekteerijat`),
    boxButton: n => `Kast „${BOX}“ (${n})`,
    boxScreenTitle: `Kast „${BOX}“`,
    boxScreenSub: 'Skanni kastist toode (või vajuta selle real), pane see näidatud riiulile ja see kaob nimekirjast.',
    boxPieces: n => `${n} tk`,
    boxEmpty: 'Kast on tühi.',
    shelvedTo: (c, sh) => `${c} → riiul ${sh} ✓`,
    onTheShelf: 'Riiulil',
    noShelf: 'riiul teadmata',
    inBoxHint: o => `See võib olla pakkimislaua kastis „${BOX}“ (tellimusest ${o}).`,
    cantFindReported: (i, o) => `OK — ${i}${o ? ` (${o})` : ''}: teatatud, juht on teadlik. Korja edasi — tellimuse üle otsustatakse pakkimisel.`,
  },
  ru: {
    scanBadge: 'Отсканируйте свой бейдж',
    scanBadgeSub: 'Поднесите бейдж к сканеру, чтобы начать сборку и упаковку',
    signOut: 'Выйти',
    hello: n => `Здравствуйте, ${n}`,
    ready: n => `Готово заказов: ${n}`,
    readyLabel: 'заказов готово',
    priorityFirst: n => `Сначала повторная отправка: ${n}`,
    howMany: 'Выберите, сколько заказов вы хотите собрать и упаковать.',
    all: 'Все',
    custom: 'Другое',
    start: 'Начать',
    starting: 'Начинаю…',
    pickup: t => `курьер ${t}`,
    noPickup: 'время курьера неизвестно',
    notReadyTitle: 'Не готовы (исправит менеджер)',
    roundsNow: 'Текущие раунды',
    noRounds: 'Открытых раундов нет',
    phase: { waiting_picker: 'ждёт сборщика', picking: 'сборка', paused: 'пауза', picked: 'собран — готов к упаковке',
             packing: 'упаковка', starting: 'начинается', closed: 'завершён', cancelled: 'отменён' },
    goToTablet: rid => `Раунд ${rid} — поставьте коробки на тележку и начните сборку на её планшете`,
    goToTabletSub: 'Накладные остаются здесь на столе для упаковки. На планшете тележки введите свой PIN.',
    notesPrinting: 'Накладные печатаются…',
    notesPrinted: 'Накладные напечатаны ✓',
    notesFailed: w => `Накладные не напечатаны: ${w}`,
    printerJam: 'Застряла бумага — печатать снова',
    printerJamManager: 'Застряла бумага? Позовите менеджера',
    ordersPage: '← Страница заказов',
    reprinted: 'Отправлено на печать снова',
    progress: (s, t) => `остановка ${s} из ${t}`,
    packHere: rid => `Раунд ${rid} собран — упакуйте его здесь`,
    packHereSub: 'Отсканируйте первую накладную.',
    packed: (p, t) => `упаковано ${p} из ${t}`,
    diverted: n => ` · ${n} выведено из раунда`,
    labelPending: orders => `${orders}: упакован, этикетка не отсканирована — отсканируйте этикетку посылки, чтобы завершить`,
    fixTitle: id => `Исправление ${id}`,
    fixProgress: (d, t) => `обработано ${d} из ${t}`,
    fixLeft: o => `Ещё сделать: ${o}`,
    endFix: 'Завершить исправление',
    endFixConfirm: 'Завершить исправление? Необработанные заказы останутся там, где они есть.',
    fixDone: n => `Исправление завершено — обработано заказов: ${n}`,
    fixManagerTitle: 'Исправить заказы — менеджер',
    fixLeftWhere: n => ` · ${n} остались на месте`,
    identifyBusy: how => (how === 'badge' ? 'Бейдж прочитан ✓ — запускаю…' : 'PIN принят ✓ — запускаю…'),
    badgeOrPin: 'Отсканируйте бейдж или введите PIN',
    packetaButton: 'Передача Packeta',
    packetaSoon: t => `Скоро пора подготовить посылки Packeta к отправке (${t})`,
    packetaDueTitle: t => `${t} — приезжает курьер Packeta`,
    packetaDueSub: n => `Передайте сейчас посылки Packeta (${n}): отсканируйте каждую. Потом продолжите упаковку.`,
    packetaStart: 'Начать передачу Packeta',
    packetaHeld: 'Сначала начните передачу Packeta',
    packetaOnIt: name => `${name} делает передачу Packeta`,
    packetaChip: (c, w) => `Packeta ${c}${w ? ` · +${w} добавить` : ''}`,
    packetaBack: 'Назад к упаковке',
    packetaDoneForNow: 'Пока всё',
    packetaCourierTook: n => `Курьер забрал ✓ (${n})`,
    packetaFor: (code, day) => `${code} · на ${day}`,
    packetaStale: (code, day) => `Курьер забрал ${code} ${day}?`,
    packetaStaleYes: 'Да, уехало',
    packetaStaleNo: 'Нет — ещё здесь',
    packetaFinishOrder: 'Сначала закончите этот заказ — потом передача Packeta',
    finish: 'Завершить раунд',
    boxCheck: 'Я проверил(а) коробки: не осталось ни товара, ни накладной',
    finishing: 'Завершаю…',
    finished: n => `Раунд завершён — обработано заказов: ${n}`,
    backToStart: 'К началу',
    chooseRound: 'Нажмите на свой раунд',
    noWaiting: 'Ни один раунд не ждёт. Начните раунд на упаковочном столе.',
    roundOf: (n, c) => `${n} · заказов: ${c}`,
    startedAt: (t, s) => `начат ${t}${s ? `, стол ${s}` : ''}`,
    someoneElse: 'Собирает кто-то другой',
    whoAreYou: 'Кто собирает?',
    enterPin: n => `${n}, введите свой PIN`,
    cancel: 'Отмена',
    yourRound: rid => `Раунд ${rid} на этой тележке`,
    continueRound: 'Продолжить',
    pausedTitle: 'Пауза — введите PIN, чтобы продолжить',
    doneTitle: 'Сборка закончена ✓',
    doneSub: 'Отвезите тележку к упаковочному столу, нажмите там «Начать упаковку» и введите свой PIN.',
    notRegistered: 'Этот экран не зарегистрирован как упаковочный стол или планшет тележки. Админ регистрирует его: Settings → Badges & rights → Devices.',
    signIn: 'Войти',
    signInTitle: 'Вход — отсканируйте бейдж или введите PIN',
    whoStarts: 'Кто начинает? Отсканируйте бейдж или введите PIN',
    packTitle: rid => `Раунд ${rid} — чтобы упаковывать, отсканируйте бейдж или введите PIN`,
    startPacking: 'Начать упаковку',
    continuePacking: 'Продолжить упаковку',
    pickedReady: rid => `Раунд ${rid} собран — начните упаковку`,
    orScanBadge: 'или отсканируйте бейдж',
    chooseName: 'Выберите своё имя',
    notYou: n => `Вы не ${n}?`,
    boxTitle: (o, why) => `Заказ ${o} убран из раунда${why} — положите его товары в коробку «${BOX}»:`,
    boxDone: 'Готово — они в коробке',
    managerTitle: (o, t) => `Заказ ${o} теперь ${t} — отдайте его накладную и эти товары менеджеру:`,
    managerDone: 'Отдано менеджеру',
    abandonedTitle: `Раунд отменён — положите всё уже собранное в коробку «${BOX}»:`,
    nothingPicked: 'Ещё ничего не собрано.',
    identifyStation: 'Представьтесь',
    identifyTablet: 'Представьтесь. Введите свой PIN.',
    startPP: 'Начать сборку и упаковку',
    pressFirst: 'Сначала нажмите «Начать сборку и упаковку» или «Начать упаковку» — затем представьтесь',
    scanNotNow: 'Сейчас сканировать нечего',
    approveTitle: 'У вас нет права упаковывать товары вручную. Позовите менеджера.',
    approveSub: 'Менеджер вводит здесь свой PIN или сканирует бейдж — ему сообщили, что вы ждёте. Менеджера нет? Отмените и используйте Issue → Can\'t find.',
    otherStation: n => `Вы начали этот раунд на столе ${n}. Упаковывать здесь?`,
    packHereBtn: 'Упаковать здесь',
    chooseRoundTitle: 'Какой раунд?',
    waitingInfo: n => (n === 1 ? '1 раунд ждёт сборщика' : `Раундов, ждущих сборщика: ${n}`),
    boxButton: n => `Коробка «${BOX}» (${n})`,
    boxScreenTitle: `Коробка «${BOX}»`,
    boxScreenSub: 'Отсканируйте товар из коробки (или нажмите на его строку), положите на указанную полку — и он уйдёт из списка.',
    boxPieces: n => `${n} шт.`,
    boxEmpty: 'Коробка пуста.',
    shelvedTo: (c, sh) => `${c} → полка ${sh} ✓`,
    onTheShelf: 'На полке',
    noShelf: 'полка неизвестна',
    inBoxHint: o => `Возможно, он в коробке «${BOX}» у упаковочного стола (из заказа ${o}).`,
    cantFindReported: (i, o) => `OK — ${i}${o ? ` (${o})` : ''}: сообщено, менеджер в курсе. Продолжайте сборку — судьба заказа решится при упаковке.`,
  },
};

export function guidedText(lang?: string | null): GuidedTexts {
  return T[(lang as Lang)] || T.en;
}
